// 플릿 지도 클러스터링 — 가까운 차량을 하나의 묶음(숫자 배지)으로 접고, 확대하면
// 자연히 다시 풀리도록 하는 순수 유틸. 지도 인스턴스에 의존하지 않도록 Web Mercator
// 화면 좌표를 직접 계산하므로, 렌더 전/테스트에서도 동일한 결과를 얻는다.
//
// 새 의존성(supercluster 등)을 추가하지 않는다 — 필요한 동작이 아래로 충분하다.

export interface GeoPoint {
  longitude: number;
  latitude: number;
}

export interface FleetCluster<T> {
  /** 안정적인 key — 구성 차량 id 를 정렬해 합친 값. */
  id: string;
  /** 묶음의 대표 위치(구성원 평균). */
  longitude: number;
  latitude: number;
  items: T[];
}

const TILE_SIZE = 512;
const MAX_LATITUDE = 85.05112878;

/** 해당 zoom 에서의 Web Mercator 화면 좌표(px). 지도 회전/기울기는 무시한다. */
export function projectToPixels(point: GeoPoint, zoom: number): { x: number; y: number } {
  const worldSize = TILE_SIZE * Math.pow(2, zoom);
  const lat = Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, point.latitude));
  const latRad = (lat * Math.PI) / 180;
  return {
    x: ((point.longitude + 180) / 360) * worldSize,
    y:
      (0.5 - Math.log(Math.tan(Math.PI / 4 + latRad / 2)) / (2 * Math.PI)) *
      worldSize,
  };
}

/**
 * 화면상 radiusPx 안에 있는 항목들을 하나의 묶음으로 만든다.
 *
 * 탐욕적으로 묶되 id 순으로 순회해 같은 입력에 항상 같은 결과를 낸다(렌더 간 깜빡임 방지).
 * zoom 이 커질수록 같은 거리라도 픽셀 간격이 벌어지므로, 확대하면 묶음이 저절로 풀린다.
 */
export function clusterByPixelDistance<T>(
  items: T[],
  getId: (item: T) => string,
  getPosition: (item: T) => GeoPoint | null,
  zoom: number,
  radiusPx: number,
): FleetCluster<T>[] {
  const located = items
    .filter((item) => getPosition(item) != null)
    .sort((a, b) => getId(a).localeCompare(getId(b)));

  const assigned = new Set<string>();
  const clusters: FleetCluster<T>[] = [];

  located.forEach((seed) => {
    const seedId = getId(seed);
    if (assigned.has(seedId)) {
      return;
    }
    assigned.add(seedId);

    const seedPosition = getPosition(seed)!;
    const seedPixels = projectToPixels(seedPosition, zoom);
    const members: T[] = [seed];

    located.forEach((candidate) => {
      const candidateId = getId(candidate);
      if (assigned.has(candidateId)) {
        return;
      }
      const candidatePixels = projectToPixels(getPosition(candidate)!, zoom);
      const dx = candidatePixels.x - seedPixels.x;
      const dy = candidatePixels.y - seedPixels.y;
      if (Math.sqrt(dx * dx + dy * dy) <= radiusPx) {
        assigned.add(candidateId);
        members.push(candidate);
      }
    });

    const sumLng = members.reduce((sum, m) => sum + getPosition(m)!.longitude, 0);
    const sumLat = members.reduce((sum, m) => sum + getPosition(m)!.latitude, 0);

    clusters.push({
      id: members.map(getId).sort().join("|"),
      longitude: sumLng / members.length,
      latitude: sumLat / members.length,
      items: members,
    });
  });

  return clusters;
}

/** [[west, south], [east, north]] — 위치가 없으면 null. */
export function getBounds(
  points: GeoPoint[],
): [[number, number], [number, number]] | null {
  if (points.length === 0) {
    return null;
  }
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  points.forEach(({ longitude, latitude }) => {
    west = Math.min(west, longitude);
    east = Math.max(east, longitude);
    south = Math.min(south, latitude);
    north = Math.max(north, latitude);
  });
  if (
    !Number.isFinite(west) ||
    !Number.isFinite(east) ||
    !Number.isFinite(south) ||
    !Number.isFinite(north)
  ) {
    return null;
  }
  return [
    [west, south],
    [east, north],
  ];
}
